import '../styles/Products.css';
import ProductItem from './products/ProductItem';

import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import { RiArrowDownSLine, RiArrowUpSLine, RiCloseLine, RiFilter3Line } from 'react-icons/ri';
import { useApiCall } from '../hooks/useApiCall';

export default function Products() {
  const { id: categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('query');
  const sortBy = searchParams.get('sort'); // Get sort parameter
  const navigate = useNavigate();
  const apiCall = useApiCall();
  
  // State management
  const [products, setProducts] = useState([]);
  const [displayCategories, setDisplayCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [availableFilters, setAvailableFilters] = useState({
    brands: [],
    modifierOptions: {},
    priceRange: { min: 0, max: 1000 }
  });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Filter states
  const [filters, setFilters] = useState({
    brands: [],
    priceRange: [0, 1000],
    modifiers: {}
  });
  const [expandedFilters, setExpandedFilters] = useState({
    brands: true,
    price: true,
    color: true,
    material: true,
    style: true
  });
  
  // Mobile filter visibility state
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  
  // New Arrivals state
  const [newArrivals, setNewArrivals] = useState([]);
  const [newArrivalsLoading, setNewArrivalsLoading] = useState(false);
  
  // Manage body scroll when mobile filters are open
  useEffect(() => {
    if (mobileFiltersVisible) {
      document.body.classList.add('filters-open');
    } else {
      document.body.classList.remove('filters-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('filters-open');
    };
  }, [mobileFiltersVisible]);
  
  // Close mobile filters when clicking outside
  const closeMobileFilters = useCallback(() => {
    setMobileFiltersVisible(false);
  }, []);

  // Fetch products from API
  const fetchProducts = useCallback((page = 1, append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    // Use the same products/get endpoint for both search and regular browsing
    const requestData = {
      page,
      limit: searchQuery ? 100 : 20, // Get more results for search
      category_id: categoryId ? parseInt(categoryId) : null,
      query: searchQuery || null,
      filters: {
        brands: filters.brands,
        price_min: filters.priceRange[0] !== availableFilters.priceRange.min ? filters.priceRange[0] : null,
        price_max: filters.priceRange[1] !== availableFilters.priceRange.max ? filters.priceRange[1] : null,
        modifiers: filters.modifiers
      }
    };

    apiCall('products/get', 'POST', requestData).then((response) => {
      if (response && response.success) {
        const newProducts = response.data.products;
        const pagination = response.data.pagination;

        if (append) {
          setProducts(prev => [...prev, ...newProducts]);
        } else {
          setProducts(newProducts);
        }

        setCurrentPage(pagination.current_page);
        setTotalPages(pagination.total_pages);
        setHasMore(pagination.has_more);
      } else {
        console.error('Failed to fetch products:', response?.message);
        setProducts([]);
      }
      
      setLoading(false);
      setLoadingMore(false);
    });
  }, [categoryId, searchQuery, filters, availableFilters, apiCall]);



  // Load more products
  const loadMoreProducts = useCallback(() => {
    if (hasMore && !loadingMore) {
      fetchProducts(currentPage + 1, true);
    }
  }, [hasMore, loadingMore, currentPage, fetchProducts]);

  // Fetch New Arrivals (only when on main products page, no category selected)
  useEffect(() => {
    if (!categoryId && !searchQuery) {
      const fetchNewArrivals = async () => {
        setNewArrivalsLoading(true);
        try {
          const response = await apiCall('products/get', 'POST', {
            page: 1,
            limit: 8,
            category_id: null,
            filters: null,
            query: null
          });
          
          if (response.success && response.data.products) {
            setNewArrivals(response.data.products);
          }
        } catch (error) {
          console.error('Error fetching new arrivals:', error);
        } finally {
          setNewArrivalsLoading(false);
        }
      };
      
      fetchNewArrivals();
    } else {
      setNewArrivals([]);
    }
  }, [categoryId, searchQuery]);

  // Initial data fetch when component mounts or category changes
  useEffect(() => {
    // Reset initialization state when category changes
    setIsInitialized(false);
    
    // If this is New Arrivals view, just fetch products sorted by newest
    if (sortBy === 'new') {
      setDisplayCategories([]);
      setCurrentCategory(null);
      setNavigation([{ id: -1, name: 'New Arrivals' }]);
      setLoading(true);
      fetchProducts(1, false);
      setIsInitialized(true);
      return;
    }
    
    // If search query exists, skip category navigation and filters
    if (searchQuery && searchQuery.trim().length >= 2) {
      setDisplayCategories([]);
      setCurrentCategory(null);
      setNavigation([]);
      setLoading(true);
      fetchProducts(1, false);
      return;
    }
    
    // Fetch categories navigation
    const navigationRequestData = {
      category_id: categoryId ? parseInt(categoryId) : null
    };
    
    apiCall('categories/navigation', 'POST', navigationRequestData).then((navigationResponse) => {
      if (navigationResponse && navigationResponse.success) {
        const { navigation_path, current_category, display_categories } = navigationResponse.data;
        
        // Build navigation breadcrumbs
        const nav = [
          { id: 0, name: 'Products' },
          ...navigation_path
        ];
        
        setNavigation(nav);
        setCurrentCategory(current_category);
        setDisplayCategories(display_categories);
      }
    });

    // Fetch filters
    setFiltersLoading(true);
    const filtersRequestData = {
      category_id: categoryId ? parseInt(categoryId) : null
    };
    
    apiCall('products/filters', 'POST', filtersRequestData).then((filtersResponse) => {
      console.log("FP", filtersResponse);
      
      if (filtersResponse && filtersResponse.success) {
        const transformedData = {
          brands: filtersResponse.data.brands || [],
          modifierOptions: filtersResponse.data.modifier_options || {},
          priceRange: {
            min: filtersResponse.data.price_range?.min || 0,
            max: filtersResponse.data.price_range?.max || 1000
          }
        };
        
        setAvailableFilters(transformedData);
        
        // Reset all filters when category changes
        setFilters({
          brands: [],
          priceRange: [transformedData.priceRange.min, transformedData.priceRange.max],
          modifiers: {}
        });

        // Now fetch products
        setLoading(true);
        const productsRequestData = {
          page: 1,
          limit: 20,
          category_id: categoryId ? parseInt(categoryId) : null,
          filters: {
            brands: [],
            price_min: null,
            price_max: null,
            modifiers: {}
          }
        };

        apiCall('products/get', 'POST', productsRequestData).then((productsResponse) => {
          if (productsResponse && productsResponse.success) {
            const newProducts = productsResponse.data.products;
            const pagination = productsResponse.data.pagination;

            setProducts(newProducts);
            setCurrentPage(pagination.current_page);
            setTotalPages(pagination.total_pages);
            setHasMore(pagination.has_more);
          }
          setLoading(false);
        });
        
        setIsInitialized(true);
      }
      setFiltersLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, searchQuery, sortBy]);

  // Fetch products when filters change (after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    // Don't refetch on filter change if we're in search mode
    if (searchQuery && searchQuery.trim().length >= 2) return;

    console.log("FETCHING")
    fetchProducts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, filters, searchQuery]);

  // Filter toggle handlers with useCallback for performance
  const toggleFilterExpansion = useCallback((filterType) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  }, []);

  const handleBrandFilter = useCallback((brand) => {
    setFilters(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand]
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, []);

  const handleModifierFilter = useCallback((modifierType, value) => {
    setFilters(prev => ({
      ...prev,
      modifiers: {
        ...prev.modifiers,
        [modifierType]: prev.modifiers[modifierType]?.includes(value)
          ? prev.modifiers[modifierType].filter(v => v !== value)
          : [...(prev.modifiers[modifierType] || []), value]
      }
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, []);

  const handlePriceRangeChange = useCallback((min, max) => {
    setFilters(prev => ({
      ...prev,
      priceRange: [min, max]
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      brands: [],
      priceRange: [availableFilters.priceRange.min, availableFilters.priceRange.max],
      modifiers: {}
    });
  }, [availableFilters.priceRange.min, availableFilters.priceRange.max]);

  // Create URL-friendly slug from category name
  const createSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s&]/g, '') // Remove special characters except spaces and &
      .replace(/\s*&\s*/g, '-and-') // Replace & with -and-
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }, []);

  // Handle category navigation
  const handleCategoryClick = useCallback((category) => {
    const slug = createSlug(category.name);
    navigate(`/products/category/${category.id}/${slug}`);
  }, [navigate, createSlug]);



  const sideBarView = useMemo( () => {
    const hasActiveFilters = filters.brands.length > 0 || 
      Object.values(filters.modifiers).some(arr => arr.length > 0) ||
      (filters.priceRange[0] !== availableFilters.priceRange.min || filters.priceRange[1] !== availableFilters.priceRange.max);

    return (
      <div className={`products-sidebar-wrapper ${mobileFiltersVisible ? 'mobile-visible' : ''}`}>
        {/* Mobile Overlay Background */}
        {mobileFiltersVisible && (
          <div className="mobile-overlay" onClick={closeMobileFilters}></div>
        )}
        
        {/* Mobile Close Button */}
        <div className="mobile-close-button">
          <button onClick={closeMobileFilters} className="close-btn">
            <RiCloseLine />
          </button>
        </div>
        
        {/* Navigation Breadcrumbs */}
        <div className="products-sidebar-navigation">
          { navigation.map( (nav, index) => (
            <span key={nav.id} className="navigation-item">
              <button 
                className="navigation-name"
                onClick={() => {
                  if (nav.id === 0) {
                    navigate('/products');
                  } else if (nav.id !== -1) {
                    handleCategoryClick(nav);
                  }
                }}
                disabled={nav.id === -1}
                style={{ cursor: nav.id === -1 ? 'default' : 'pointer' }}
              >
                { nav.name }
              </button>
              { index < navigation.length - 1 && <span className="navigation-arrow">→</span> }
            </span>
          )) }
        </div>
        
        <div className="products-sidebar">
          {/* Category Section */}
          <div className="products-sidebar-category">
            <div className="products-sidebar-category-title">{ currentCategory ? currentCategory.name : 'All Products' }</div>
            <div className="products-sidebar-category-items">
                { displayCategories && displayCategories.length > 0 ? displayCategories.map( (item) => (
                    <div key={item.id} className="products-sidebar-category-item">
                        <button 
                            className={`products-sidebar-category-item-name ${
                                !item.children && item.selected ? 'selected' : ''
                            }`}
                            onClick={() => handleCategoryClick(item)}
                        >
                            <span className="category-name">{ item.name }</span>
                            { item.children && (
                                <span className="category-arrow">→</span>
                            )}
                        </button>
                        <div className="products-sidebar-category-item-subitems">
                            { item.children && item.children.map( (subitem) => (
                                <button 
                                    key={subitem.id} 
                                    className={`products-sidebar-category-item-subitem ${subitem.selected ? 'selected' : ''}`}
                                    onClick={() => handleCategoryClick(subitem)}
                                >
                                    { subitem.name }
                                </button>
                            )) }
                        </div>
                    </div>
                )) : (
                    <div className="products-sidebar-main-category-selected">
                        No subcategories available
                    </div>
                )}
            </div>
          </div>

          {/* Filters Section */}
          <div className="products-sidebar-filters">
            <div className="filters-header">
              <h3>Filters</h3>
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearAllFilters}>
                  <RiCloseLine />
                  Clear All
                </button>
              )}
            </div>

            {/* Brand Filter */}
            <div className="filter-section">
              <button 
                className="filter-header"
                onClick={() => toggleFilterExpansion('brands')}
              >
                <span>Brand</span>
                {expandedFilters.brands ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
              </button>
              {expandedFilters.brands && (
                <div className="filter-options">
                  {availableFilters.brands.map(brand => (
                    <label key={'filter' + brand} className="filter-option">
                      <input
                        type="checkbox"
                        checked={filters.brands.includes(brand)}
                        onChange={() => handleBrandFilter(brand)}
                      />
                      <span className="checkmark"></span>
                      <span className="filter-label">{brand}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Price Range Filter */}
            <div className="filter-section">
              <button 
                className="filter-header"
                onClick={() => toggleFilterExpansion('price')}
              >
                <span>Price Range</span>
                {expandedFilters.price ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
              </button>
              {expandedFilters.price && (
                <div className="filter-options">
                  <div className="price-range-container">
                    <div className="price-inputs">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.priceRange[0]}
                        onChange={(e) => handlePriceRangeChange(parseInt(e.target.value) || 0, filters.priceRange[1])}
                        className="price-input"
                      />
                      <span className="price-separator">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.priceRange[1]}
                        onChange={(e) => handlePriceRangeChange(filters.priceRange[0], parseInt(e.target.value) || availableFilters.priceRange.max)}
                        className="price-input"
                      />
                    </div>
                    <div className="price-range-info">
                      ${availableFilters.priceRange.min} - ${availableFilters.priceRange.max}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dynamic Modifier Filters */}
            {Object.entries(availableFilters.modifierOptions).map(([modifierType, options]) => (
              <div key={modifierType} className="filter-section">
                <button 
                  className="filter-header"
                  onClick={() => toggleFilterExpansion(modifierType)}
                >
                  <span>{modifierType.charAt(0).toUpperCase() + modifierType.slice(1)}</span>
                  {expandedFilters[modifierType] ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
                </button>
                {expandedFilters[modifierType] && (
                  <div className="filter-options">
                    {options.map(option => (
                      <label key={option} className="filter-option">
                        <input
                          type="checkbox"
                          checked={filters.modifiers[modifierType]?.includes(option) || false}
                          onChange={() => handleModifierFilter(modifierType, option)}
                        />
                        <span className="checkmark"></span>
                        <span className="filter-label">{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }, [currentCategory, navigation, displayCategories, availableFilters, filters, expandedFilters, clearAllFilters, toggleFilterExpansion, handleBrandFilter, handleModifierFilter, handlePriceRangeChange, mobileFiltersVisible, closeMobileFilters, handleCategoryClick, navigate])

  return (
    <div className="products-page">

        { sideBarView }

        <div className="products-items">
            {/* Mobile Navigation - Shown above header on mobile */}
            <div className="products-mobile-navigation">
              { navigation.map( (nav, index) => (
                <span key={nav.id} className="navigation-item">
                  <button 
                    className="navigation-name"
                    onClick={() => {
                      if (nav.id === 0) {
                        navigate('/products');
                      } else if (nav.id !== -1) {
                        handleCategoryClick(nav);
                      }
                    }}
                    disabled={nav.id === -1}
                    style={{ cursor: nav.id === -1 ? 'default' : 'pointer' }}
                  >
                    { nav.name }
                  </button>
                  { index < navigation.length - 1 && <span className="navigation-arrow">→</span> }
                </span>
              )) }
            </div>
            
            <div className="products-items-header">
              <div className="products-items-header-title">
                Products
                {searchQuery && (
                  <span className="category-name"> - Search: "{searchQuery}"</span>
                )}
                {!searchQuery && sortBy === 'new' && (
                  <span className="category-name"> - New Arrivals</span>
                )}
                {!searchQuery && !sortBy && currentCategory && (
                  <span className="category-name"> - {currentCategory.name}</span>
                )}
              </div>
              
              {/* Mobile Filter Toggle Button */}
              <button 
                className="mobile-filter-toggle"
                onClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
              >
                <RiFilter3Line />
                Filters
              </button>
            </div>

            {loading ? (
              <div className="products-loading">
                <p>Loading products...</p>
              </div>
            ) : (
              <>
                <div className="products-items-items">
                  { products.length > 0 ? (
                    products.map( (product) => (
                      <ProductItem key={product.id} product={product} />
                    ))
                  ) : (
                    <div className="no-products-found">
                      <p>No products found matching your filters.</p>
                      {/* <button className="clear-filters-btn" onClick={clearAllFilters}>
                        Clear Filters
                      </button> */}
                    </div>
                  )}
                </div>

                {/* New Arrivals Section - Only show on main products page, not when viewing New Arrivals */}
                {!categoryId && !searchQuery && !sortBy && newArrivals.length > 0 && (
                  <div className="new-arrivals-section">
                    <div className="new-arrivals-header">
                      <h2 className="new-arrivals-title">New Arrivals</h2>
                      <p className="new-arrivals-subtitle">Check out our latest products</p>
                    </div>
                    <div className="new-arrivals-grid">
                      {newArrivalsLoading ? (
                        <div className="new-arrivals-loading">
                          <p>Loading new arrivals...</p>
                        </div>
                      ) : (
                        newArrivals.map((product) => (
                          <ProductItem key={product.id} product={product} />
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Load More Button */}
                {hasMore && (
                  <div className="products-load-more">
                    <button 
                      className="load-more-btn" 
                      onClick={loadMoreProducts}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading...' : 'Load More Products'}
                    </button>
                  </div>
                )}
              </>
            )}
        </div>

    </div>
  );
}
